import { ThemeComponentProps } from '@chakra-ui/theme';
import { getColor, mode, transparentize } from '@chakra-ui/theme-tools';
import { mergeWith } from '@chakra-ui/utils';
import { fontWeights } from '../foundations/typography';
import { textStyles } from '../textStyles';
import { Dict } from '../types';
import { Menu } from './menu';

const parts = [
  'option',
  'item',
  'list',
  'decorator_front',
  'decorator_end',
  'input',
  'selectioncount',
  'groupTitle',
];

const getTextStyle = (size: unknown): Dict => {
  if (size === 'sm') {
    return textStyles.sm;
  }

  if (size === 'lg') {
    return textStyles.lg;
  }

  return textStyles.md;
};

const getRTLPadding = (size: unknown): Dict => {
  if (size === 'sm') {
    return { paddingRight: '1.75rem' };
  }

  if (size === 'lg') {
    return { paddingRight: '3.5rem' };
  }

  // md is default
  return { paddingRight: '2.5rem' };
};

const getTitlePadding = (size: unknown): Dict => {
  if (size === 'sm') {
    return { px: '2' };
  }

  if (size === 'lg') {
    return { px: '4' };
  }

  // md is default
  return { px: '3' };
};

const getPadding = (size: unknown): Dict => {
  if (size === 'sm') {
    return { py: '1.5', px: '3' };
  }

  if (size === 'lg') {
    return { py: '2.5', px: '3' };
  }

  // md is default
  return { py: '2', px: '3' };
};

const baseStyle = (props: ThemeComponentProps): Dict => {
  const { theme, colorScheme: c = 'light-blue', errorBorderColor } = props;

  const ec = (errorBorderColor as string) || 'error.400';

  const overrides = {
    option: {
      display: 'flex',
      flexDirection: 'row',
      width: '100%',
    },

    groupTitle: {
      ...getTextStyle(props.size),
      fontWeight: fontWeights.medium,
      ...getTitlePadding(props.size),
    },

    list: {
      bg: mode('white', 'slate.900')(props) as string,
      boxShadow: '2',
      color: 'inherit',
      borderRadius: 'base',
      borderWidth: '2px',
      zIndex: 'menu',
      marginLeft: '-1px',
      marginRight: '-1px',
      minWidth: 'unset',
      overflowX: 'hidden',
      overflowY: 'auto',
      _focusWithin: {
        borderColor: 'information.400',
      },
    },

    // for DropdownOption: Button
    item: {
      textDecoration: 'none',
      color: 'inherit',
      userSelect: 'none',
      display: 'flex',
      width: '100%',
      alignItems: 'center',
      textAlign: 'start',
      flex: '0 0 auto',
      outline: 0,
      _checked: {
        backgroundColor: `${c}.50`,
      },
      ...getPadding(props.size),
    },

    // for DropdownOption: Button > content
    item_decorator_container: {
      display: 'flex',
      flexDirection: 'row',
      width: '100%',
      ...getTextStyle(props.size),
    },
    item_decorator_content: {
      display: 'flex',
      flex: 1,
      ...getTextStyle(props.size),
    },
    item_decorator_front: {
      display: 'flex',
      marginEnd: '0.75rem',
      pointerEvents: 'none',
      padding: '0.2rem 0px',
    },
    item_decorator_end: {
      display: 'flex',
      pointerEvents: 'none',
      marginEnd: '0.1rem',
      marginStart: '1rem',
      justifyContent: 'center',
      width: '1rem',
      fontSize: 'sm',
      padding: '0.2rem 0px',
      alignItems: 'center', // overrideable by selectionIndicatorAlignment prop
    },

    input: {
      transition: 'all 0.2s',
      borderRadius: 'base',
      width: '100%',
      minWidth: 0,
      outline: 0,
      position: 'relative',
      appearance: 'none',
      borderWidth: '1px',
      boxShadow: '1',
      pointerEvents: 'all',
      textAlign: 'start',

      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',

      _hover: {
        borderColor: 'slate.300',
        boxShadow: `0 0 0 1px ${getColor(theme, 'slate.300') as string}`,
      },
      _focusVisible: {
        borderColor: 'information.400',
        boxShadow: `0 0 0 1px ${getColor(theme, 'information.400') as string}`,
      },

      // quick fix for RTL
      '[dir="rtl"] &': {
        ...getRTLPadding(props.size),
      },

      // force the _hover style from above when the menu is open:
      '&[data-active]': {
        borderColor: 'slate.300',
        boxShadow: `0 0 0 1px ${getColor(theme, 'slate.300') as string}`,
        _hover: {
          borderColor: 'slate.300',
          boxShadow: `0 0 0 1px ${getColor(theme, 'slate.300') as string}`,
        },
      },
      _readOnly: {
        userSelect: 'none',
        _hover: {
          borderColor: mode('slate.300', transparentize('slate.900', 0.05)(props))(props) as string,
          boxShadow: `0 0 0 1px ${getColor(theme, 'slate.300') as string}`,
          cursor: 'pointer',
        },
      },
      _invalid: {
        borderColor: getColor(theme, ec) as string,
        boxShadow: `0 0 0 1px ${getColor(theme, ec) as string}`,
      },
      _disabled: {
        opacity: 0.5,
        cursor: 'not-allowed',
        background: 'slate.30',
        boxShadow: '0',
        _hover: {
          borderColor: 'inherit',
          boxShadow: '0',
        },
      },
    },

    // inline selection tag
    selectioncount: {
      display: 'inline-flex',
      position: 'relative',
      zIndex: 2,
      float: 'left',
      height: 0,
    },
    selectioncountLabel: {
      fontVariantNumeric: 'tabular-nums',
    },
  };

  return mergeWith(Menu.baseStyle(props), overrides) as Dict;
};

const sizes = {
  sm: {
    item: {
      ...textStyles.sm,
    },
    input: {
      ...textStyles.sm,
      paddingX: '2',
      h: '8',
      '&[data-hasinlineselection]': {
        paddingLeft: '3.25rem',
      },
    },
    selectioncount: {
      top: '-1rem',
      left: '0.5rem',
    },
  },
  md: {
    item: {
      ...textStyles.md,
    },
    input: {
      ...textStyles.md,
      paddingX: '3',
      h: '12',
      '&[data-hasinlineselection]': {
        paddingLeft: '4rem',
      },
    },
    selectioncount: {
      top: '-1.5rem',
      left: '0.5rem',
    },
  },
  lg: {
    item: {
      ...textStyles.lg,
    },
    input: {
      ...textStyles.lg,
      paddingX: '4',
      h: '16',
      '&[data-hasinlineselection]': {
        paddingLeft: '4.75rem',
      },
    },
    selectioncount: {
      top: '-2rem',
      left: '0.5rem',
      minWidth: '4rem',
    },
  },
};

const defaultProps = {
  colorScheme: 'light-blue',
  size: 'md',
};

export const Dropdown = {
  defaultProps,
  sizes,
  parts,
  baseStyle,
};
